import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError, formatCOP, formatDate } from "../lib/apiClient";
import { Disclaimer } from "../components/Disclaimer";
import CaseFlowStepper from "../components/CaseFlowStepper";
import CaseSummaryCard from "../components/CaseSummaryCard";
import FlowGuide from "../components/FlowGuide";
import { toast } from "sonner";
import { VariantN2Selector } from "./Nivel2RequestPage/VariantN2Selector";
import { ScopeSidebar } from "./Nivel2RequestPage/ScopeSidebar";
import { IntakeN2Form } from "./Nivel2RequestPage/IntakeN2Form";
import { useN2Offer } from "../lib/useN2Offer";

/**
 * Fase 11.2 — Solicitud Kit Documental (Level 2).
 *
 * Página que muestra alcance + precio + formulario de intake N2.
 * Al enviar, persiste el intake y luego inicia el pago Nivel 2.
 *
 * Gates (frontend mirrors backend):
 * - CTA solo visible si state ∈ {nivel1_delivered, nivel2_offer_sent, nivel2_payment_pending}.
 * - Si state >= nivel2_paid → redirect a /cases/{id} (no edición).
 * - Si state pre-nivel1_delivered → mensaje "completa el Diagnóstico primero".
 */

const REQUIRED_FIELDS = [
  "presupuesto_definitivo_cop",
  "plazo_ejecucion_meses",
  "lugar_ejecucion_departamento",
  "lugar_ejecucion_municipio",
  "productos_servicios_detalle",
  "cantidades_unidades",
  "modalidad_contratacion",
];

const INITIAL_FORM = {
  presupuesto_definitivo_cop: "",
  plazo_ejecucion_meses: "",
  lugar_ejecucion_departamento: "",
  lugar_ejecucion_municipio: "",
  productos_servicios_detalle: "",
  cantidades_unidades: "",
  modalidad_contratacion: "desconocida",
  requisitos_tecnicos_minimos: "",
  forma_pago_contratista: "por_definir",
  forma_pago_detalle: "",
  garantias_exigidas: [],
  garantias_detalle: "",
  requiere_supervision: false,
  requiere_interventoria: false,
  supervision_interventoria_detalle: "",
  comentarios_adicionales: "",
};

export default function Nivel2RequestPage() {
  const n2Offer = useN2Offer();
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseDoc, setCaseDoc] = useState(null);
  const [scope, setScope] = useState(null);
  const [existingIntake, setExistingIntake] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [confirmFinal, setConfirmFinal] = useState(false);

  // Fase 12: variant selection for N2
  const [variantSelected, setVariantSelected] = useState(null);
  const [variantDisclaimerAccepted, setVariantDisclaimerAccepted] = useState(false);
  const [submittingVariant, setSubmittingVariant] = useState(false);
  const [showVariantSelector, setShowVariantSelector] = useState(false);

  // Sprint 2 Fase 2+3: base diagnosis selection
  const [diagnosisList, setDiagnosisList] = useState([]);
  const [selectedDiagnosisId, setSelectedDiagnosisId] = useState(null);

  // Iter18 — Cuota gratuita N2 (kits_remaining)
  const [quota, setQuota] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: c } = await api.get(`/cases/${id}`);
      setCaseDoc(c);
      // Iter18 — cuota gratuita (best-effort)
      api.get(`/me/quota`).then((r) => setQuota(r.data || null)).catch(() => {});
      // Estados desde donde se puede solicitar/editar
      const allowed = ["nivel1_delivered", "nivel2_offer_sent", "nivel2_payment_pending"];
      const locked = [
        "nivel2_paid", "nivel2_processing", "nivel2_needs_human_review",
        "nivel2_revision_requested", "nivel2_delivered", "closed",
      ];
      if (locked.includes(c.state)) {
        toast.info("Esta solicitud ya está en proceso o entregada.");
        navigate(`/cases/${id}`, { replace: true });
        return;
      }
      if (!allowed.includes(c.state)) {
        setErrorMsg(
          "Para solicitar el Kit Documental, primero debe estar entregado tu Diagnóstico."
        );
        setLoading(false);
        return;
      }

      // Fase 12: si no hay variant_nivel2, mostrar selector
      if (c.variant_nivel2) {
        setVariantSelected(c.variant_nivel2);
        setShowVariantSelector(false);
      } else {
        setShowVariantSelector(true);
      }

      // Sprint 2 Fase 2+3: cargar lista de diagnósticos N1
      try {
        const { data: diagnosesResp } = await api.get(`/cases/${id}/diagnoses`);
        const approvedDiagnoses = (diagnosesResp.items || []).filter(
          (d) => ["approved", "skipped_by_user_auto_variant"].includes(d.human_review_status)
        );
        setDiagnosisList(approvedDiagnoses);
        
        // Pre-seleccionar el diagnóstico actual (is_current=true)
        const currentDiagnosis = approvedDiagnoses.find((d) => d.is_current);
        if (currentDiagnosis) {
          setSelectedDiagnosisId(currentDiagnosis.id);
        } else if (approvedDiagnoses.length === 1) {
          // Si solo hay uno, seleccionarlo
          setSelectedDiagnosisId(approvedDiagnoses[0].id);
        }
      } catch (diagErr) {
        if (process.env.NODE_ENV === "development") {
          console.warn("No se pudo cargar diagnósticos", diagErr);
        }
      }

      // Carga scope (alcance + precio + campos)
      try {
        const { data: s } = await api.get(`/cases/${id}/nivel2/scope`);
        setScope(s);
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.warn("No se pudo cargar scope", e);
        }
      }

      // Si ya hay intake (editando), prellenar
      try {
        const { data: existing } = await api.get(`/cases/${id}/nivel2/intake`);
        setExistingIntake(existing);
        setForm((f) => ({
          ...f,
          ...Object.fromEntries(
            Object.entries(existing).filter(([k]) => k in INITIAL_FORM)
          ),
        }));
      } catch (err) {
        // 404 esperado si no hay intake aún (primera vez)
        if (process.env.NODE_ENV === "development" && err?.response?.status !== 404) {
          console.warn("Intake N2 load failed:", err);
        }
      }
    } catch (e) {
      setErrorMsg(formatApiError(e?.response?.data?.detail) || "No se pudo cargar el caso");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleGarantia = (g) => {
    setForm((f) => {
      const has = f.garantias_exigidas.includes(g);
      return {
        ...f,
        garantias_exigidas: has
          ? f.garantias_exigidas.filter((x) => x !== g)
          : [...f.garantias_exigidas, g],
      };
    });
  };

  const validateBasic = () => {
    for (const k of REQUIRED_FIELDS) {
      if (form[k] === "" || form[k] === null || form[k] === undefined) {
        return `El campo "${k}" es obligatorio.`;
      }
    }
    if (Number(form.presupuesto_definitivo_cop) <= 0) {
      return "El presupuesto definitivo debe ser mayor a 0.";
    }
    if (Number(form.plazo_ejecucion_meses) < 0.25) {
      return "El plazo mínimo es 0.25 meses (1 semana).";
    }
    if ((form.productos_servicios_detalle || "").trim().length < 50) {
      return "El detalle de productos/servicios debe tener al menos 50 caracteres.";
    }
    if ((form.cantidades_unidades || "").trim().length < 20) {
      return "Cantidades y unidades debe tener al menos 20 caracteres.";
    }
    return null;
  };

  const handleVariantN2Submit = async () => {
    if (!variantSelected) {
      toast.error("Debes elegir una variante");
      return;
    }
    if (variantSelected === "auto" && !variantDisclaimerAccepted) {
      toast.error("Debes aceptar el disclaimer para continuar con la variante automatizada");
      return;
    }
    setSubmittingVariant(true);
    try {
      const payload = {
        variant: variantSelected,
        disclaimer_accepted: variantSelected === "auto" ? variantDisclaimerAccepted : false,
      };
      
      // Sprint 2 Fase 2+3: incluir base_diagnosis_report_id si hay selección
      if (selectedDiagnosisId) {
        payload.base_diagnosis_report_id = selectedDiagnosisId;
      }
      
      await api.post(`/cases/${id}/accept-nivel2-offer`, payload);
      toast.success("Variante seleccionada. Ahora completa la información del Kit.");
      setShowVariantSelector(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo guardar la variante");
    } finally {
      setSubmittingVariant(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setErrorMsg("");
    if (!confirmFinal) {
      setErrorMsg("Debes confirmar que la información es correcta antes de continuar.");
      return;
    }
    const err = validateBasic();
    if (err) {
      setErrorMsg(err);
      return;
    }
    setSubmitting(true);
    try {
      // 1) Persistir intake N2
      const payload = {
        ...form,
        presupuesto_definitivo_cop: Number(form.presupuesto_definitivo_cop),
        plazo_ejecucion_meses: Number(form.plazo_ejecucion_meses),
      };
      const { data: intakeResp } = await api.post(`/cases/${id}/nivel2/intake`, payload);
      toast.success(
        intakeResp.transitioned
          ? "Información guardada. Continuemos con el pago."
          : `Información actualizada (versión ${intakeResp.version}).`
      );

      // 2) Iniciar pago N2 (idempotente)
      try {
        await api.post(`/cases/${id}/payments/nivel2/init`, {});
      } catch (payErr) {
        // Si falla por estado (ya está en otro), continuar igual
        if (process.env.NODE_ENV === "development") {
          console.warn("Payment init warning:", payErr);
        }
      }

      // 3) Navegar a la página de pago N2
      navigate(`/cases/${id}/payment-n2`);
    } catch (e) {
      setErrorMsg(formatApiError(e?.response?.data?.detail) || "No se pudo guardar la solicitud");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-12">
        <p className="text-sm text-gray-500">Cargando…</p>
      </div>
    );
  }

  if (errorMsg && !caseDoc) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-12">
        <p className="text-rose-700 bg-rose-50 border border-rose-200 p-4 rounded-sm">{errorMsg}</p>
        <div className="mt-4">
          <Link to={`/cases/${id}`} className="text-primary text-sm hover:underline">
            ← Volver al caso
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="nivel2-request-page">
      <div className="mb-2">
        <Link to={`/cases/${id}`} className="text-xs text-gray-500 hover:text-primary">
          ← Volver al caso
        </Link>
      </div>

      {/* Iter20 — Summary + Stepper para orientación */}
      <CaseSummaryCard caseDoc={caseDoc} showLink={false} />
      <div className="mb-4">
        <CaseFlowStepper caseId={id} state={caseDoc?.state || "nivel2_offer_sent"} />
      </div>

      {/* Iter25 — Guía de auto-orientación N2 */}
      <div className="mb-6">
        <FlowGuide level="n2" quota={quota} state={caseDoc?.state} caseId={id} />
      </div>

      <p className="text-[11px] uppercase tracking-[0.22em] text-primary font-medium mb-1">
        Solicitud · Kit Documental
      </p>
      <h1 className="font-serif text-3xl text-gray-900 mb-1">
        {scope?.title || "Kit Documental"}
      </h1>
      <p className="text-2xl font-serif text-gray-900 mb-6">
        {variantSelected
          ? formatCOP(variantSelected === "auto" ? n2Offer.price_auto_cop : n2Offer.price_reviewed_cop)
          : <span className="text-gray-500">{formatCOP(n2Offer.price_auto_cop)} - {formatCOP(n2Offer.price_reviewed_cop)}</span>}
      </p>

      {/* Sprint 2 Fase 2+3: Selector de diagnóstico base (solo si >1 aprobado) */}
      {diagnosisList.length > 1 && (
        <section
          className="mb-8 border border-gray-300 bg-gray-50 p-6"
          data-testid="diagnosis-base-selector"
        >
          <h2 className="font-serif text-xl text-gray-900 mb-2">Diagnóstico base</h2>
          <p className="text-sm text-gray-600 mb-4">
            Tienes múltiples diagnósticos Nivel 1 aprobados. Elige cuál deseas usar como base para
            generar el Kit Documental.
          </p>

          <div className="space-y-3">
            {diagnosisList.map((diag) => (
              <label
                key={diag.id}
                className={`border-2 p-4 rounded-sm cursor-pointer transition flex items-start gap-3 ${
                  selectedDiagnosisId === diag.id
                    ? "border-primary bg-primary/5"
                    : "border-gray-300 hover:border-gray-400 bg-white"
                }`}
                data-testid={`diagnosis-base-version-${diag.version}`}
              >
                <input
                  type="radio"
                  name="base_diagnosis"
                  value={diag.id}
                  checked={selectedDiagnosisId === diag.id}
                  onChange={() => setSelectedDiagnosisId(diag.id)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-medium text-gray-900">Versión {diag.version}</span>
                    {diag.is_current && (
                      <span className="text-[9px] uppercase bg-blue-50 text-blue-800 border border-blue-300 px-2 py-1">
                        Actual
                      </span>
                    )}
                    {diag.auto_variant && (
                      <span className="text-[9px] uppercase bg-amber-50 text-amber-800 border border-amber-300 px-2 py-1">
                        Automatizado
                      </span>
                    )}
                    {!diag.auto_variant && (
                      <span className="text-[9px] uppercase bg-emerald-50 text-emerald-800 border border-emerald-300 px-2 py-1">
                        Con revisión humana experta
                      </span>
                    )}
                    {diag.has_kit && (
                      <span className="text-[9px] uppercase bg-violet-50 text-violet-800 border border-violet-300 px-2 py-1">
                        Kit generado ({(diag.kits_generated || []).map((k) => `v${k.version}`).join(", ")})
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600">
                    Creado el {formatDate(diag.created_at)}
                    {diag.reviewed_at && ` • Revisado el ${formatDate(diag.reviewed_at)}`}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Modelo: {diag.model_used || "N/A"}
                  </p>
                  {(diag.prevalidation_version_used || diag.intake_version_used) && (
                    <p className="text-[10px] text-gray-500 mt-1">
                      Basado en:
                      {diag.prevalidation_version_used && ` Prevalid. v${diag.prevalidation_version_used}`}
                      {diag.intake_version_used && ` · Borrador v${diag.intake_version_used}`}
                    </p>
                  )}
                  {diag.has_kit && (
                    <p className="text-[10px] text-violet-700 mt-1">
                      Este diagnóstico ya tiene Kit Documental generado. Si solicitas uno nuevo,
                      se creará una nueva versión del Kit sobre este mismo diagnóstico.
                    </p>
                  )}
                </div>
              </label>
            ))}
          </div>

          {selectedDiagnosisId && (
            <div
              className="mt-4 border border-blue-300 bg-blue-50 p-3"
              data-testid="diagnosis-base-selected-banner"
            >
              <p className="text-sm text-blue-900">
                ✓ Diagnóstico Versión{" "}
                {diagnosisList.find((d) => d.id === selectedDiagnosisId)?.version} seleccionado
                como base
              </p>
            </div>
          )}
        </section>
      )}

      {/* Iter18/19 — Aviso de cuota gratuita N2 disponible (visible desde el inicio, antes de elegir variante) */}
      {quota && (quota.kits_remaining ?? 0) > 0 && (
        <div
          className="mb-6 border-2 border-emerald-300 bg-emerald-50/60 p-4 flex items-start gap-3"
          data-testid="quota-n2-available-notice"
        >
          <div className="flex-shrink-0 w-10 h-10 rounded-sm bg-emerald-100 border border-emerald-300 flex items-center justify-center">
            <span className="font-serif text-lg text-emerald-800">★</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold mb-1">
              Tienes cuota gratuita disponible
            </p>
            <p className="text-sm text-gray-800 leading-relaxed">
              Te quedan <strong>{quota.kits_remaining}</strong> de {quota.kits_limit} cuotas
              gratuitas de Kit Documental este mes ({quota.year_month}).
              Al finalizar y continuar al pago, podrás elegir entre <strong>usar 1 cuota gratuita</strong> (a costo $0)
              o <strong>pagar con MercadoPago/transferencia</strong>.
            </p>
            <p className="text-[11px] text-emerald-900/80 mt-2">
              💡 Si usas tu cuota, el Kit Documental se procesa automáticamente sin necesidad de comprobante.
            </p>
          </div>
        </div>
      )}

      {/* Selector de variante (Fase 12) */}
      {showVariantSelector && (
        <VariantN2Selector
          variantSelected={variantSelected}
          setVariantSelected={setVariantSelected}
          variantDisclaimerAccepted={variantDisclaimerAccepted}
          setVariantDisclaimerAccepted={setVariantDisclaimerAccepted}
          submittingVariant={submittingVariant}
          onSubmit={handleVariantN2Submit}
        />
      )}

      {/* Banner de variante elegida */}
      {!showVariantSelector && variantSelected && (
        <div
          className={`border p-4 mb-6 ${variantSelected === "auto" ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`}
          data-testid="variant-n2-selected-banner"
        >
          <p className="text-sm font-medium text-gray-900">
            Plan elegido: {variantSelected === "auto" ? "Kit Documental Automatizado (sin revisión humana)" : "Kit con revisión humana experta"}
            {" — "}
            {formatCOP(variantSelected === "auto" ? n2Offer.price_auto_cop : n2Offer.price_reviewed_cop)}
          </p>
          {variantSelected === "auto" && (
            <p className="text-xs text-amber-900 mt-1">
              Sin revisión humana — el paquete se descargará inmediatamente tras confirmarse el pago. Responsabilidad del usuario.
            </p>
          )}
        </div>
      )}

      {/* Resto de la página (intake + submit) — solo mostrar si variant ya elegido */}
      {!showVariantSelector && (
      <>
      {/* Alcance */}
      <ScopeSidebar scope={scope} />

      {/* Disclaimer Ley 1581 */}
      <div className="mb-6">
        <Disclaimer variant="banner" />
      </div>

      {/* Formulario intake */}
      <IntakeN2Form
        form={form}
        setField={setField}
        toggleGarantia={toggleGarantia}
        confirmFinal={confirmFinal}
        setConfirmFinal={setConfirmFinal}
        errorMsg={errorMsg}
        submitting={submitting}
        onSubmit={handleSubmit}
        caseId={id}
        scopePrice={scope?.price_cop}
        existingIntake={existingIntake}
      />
      </>
      )}
    </div>
  );
}
